import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { firstValueFrom, of } from 'rxjs';
import { ChartType, ChartConfiguration } from 'chart.js';

import { ChartDataService } from '@Services';
import { loggingService } from '@Services';

import { ChartComponent } from '../chart/chart.component';
import { CustomDataPoint } from '../chart/chart.component';

interface Filter {
  total_text: string;
  total: string;
  value: string;
  link: string;
  label: string;
  custom_property_name_2: string;
  custom_property_value_2: string;
  filtered_total_text: string; //this is to hold the label for the total number of records that matched the search query
  filtered_total_value: string; //this is to hold the actual number of records that matched the search query
}

import { SubSink } from 'subsink';

@Component({
  selector: 'app-home-card',
  standalone: true,
  templateUrl: './home-card.component.html',
  styleUrl: './home-card.component.css',
  imports: [ChartComponent, CommonModule],
})
export class HomeCardComponent implements OnInit, OnDestroy {
  @Input() title!: string;
  @Input() ref!: string; //can be dynamic, random or array ( dynamic is manually setting 2 bars, random is using random values for 2 bars, array is using an array of filters allowing an object to be passed containing X number of bar data)
  @Input() summary_search_parameter_for_charts!: string;
  @Input() main_link_for_chart!: string;
  @Input() description: string | null = null;
  @Input() link1!: string;
  @Input() link2!: string;
  @Input() totalFilter1!: string;
  @Input() valueFilter1!: string;
  @Input() totalFilter2!: string;
  @Input() valueFilter2!: string;
  //new filters object
  @Input() filters!: Filter[];
  error: string | null = null;
  chartDataArray: {
    chartData: ChartConfiguration['data'];
    customData: CustomDataPoint[][];
    chartOptions: ChartConfiguration['options'] | undefined;
    chartType: ChartType;
  }[] = [];

  private subs = new SubSink();

  constructor(
    private chartDataService: ChartDataService,
    private router: Router,
  ) {}

  navigateToLink() {
    this.router.navigateByUrl(this.main_link_for_chart).then();
  }

  ngOnInit() {
    //This is what is called when the there is an array of objects containing the filters, passed in 'and' we just set a random number value - no api call
    if (this.filters && this.filters.length > 0 && this.ref == 'randomArray') {
      const randomValue1$ = of(Math.floor(Math.random() * 100));
      loggingService.debugLog('this.title', this.title);
      this.chartDataService.generateRandomValuesForArray(this.title, this.filters).subscribe({
        next: async (data) => {
          try {
            // Using firstValueFrom instead of toPromise
            const randomValue = await firstValueFrom(randomValue1$);
            this.chartDataArray = this.chartDataService.setDynamicChartDataArray(
              this.title,
              randomValue ?? 0,
              data.filters,
            );
          } catch (err) {
            console.error('Error getting random value:', err);
          }
        },
        error: (error) => {
          this.error = 'Error fetching chart data';
          console.error('Error fetching chart data:', error.message);
        },
      });

      return;
    }
    //this is when we are passing in 2 sets of data manually - no api call
    else if (this.ref == 'random') {
      const randomValue1$ = of(Math.floor(Math.random() * 100));
      this.subs.add(
        this.chartDataService.generateRandomValues(this.title, this.link1, this.link2).subscribe({
          next: async (data) => {
            try {
              // Using firstValueFrom instead of toPromise
              const value = await firstValueFrom(randomValue1$);
              this.chartDataArray = this.chartDataService.setChartDataArray(
                value ?? 0, // default to 0 if value is undefined
                data.title,
                data.value1,
                data.total1,
                data.value2,
                data.total2,
                data.link1,
                data.link2,
              );
            } catch (err) {
              console.error('Error getting random value:', err);
            }
          },
          error: (error) => {
            this.error = 'Error fetching chart data';
            console.error('Error fetching chart data:', error.message);
          },
        }),
      );
      return;
    } //this is when we pass 2 sets of data in and make an api call to get the data for each set - it makes an api call but is limited to only 2 bars of data
    else if (this.ref == 'dynamic') {
      const randomValue = Math.floor(Math.random() * 100);
      this.subs.add(
        this.chartDataService
          .callApi(
            this.title,
            this.totalFilter1,
            this.valueFilter1,
            this.totalFilter2,
            this.valueFilter2,
            this.link1,
            this.link2,
          )
          .subscribe({
            next: (data) => {
              this.chartDataArray = this.chartDataService.setChartDataArray(
                randomValue,
                data.title,
                data.value1,
                data.total1,
                data.value2,
                data.total2,
                data.link1,
                data.link2,
              );
            },
            error: (error) => {
              this.error = 'Error fetching chart data';
              console.error('Error fetching chart data:', error.message);
            },
          }),
      );
      return;
    }
    //this is when we pass in an array of objects with filters and make an api call to get the data for each filter - we can send through as many objects as we like to make X bars on the chart
    else if (this.ref == 'dynamicArray') {
      const randomValue = Math.floor(Math.random() * 100);
      this.subs.add(
        this.chartDataService.callApiForArray(this.title, this.filters).subscribe({
          next: (data) => {
            this.chartDataArray = this.chartDataService.setDynamicChartDataArray(
              this.title,
              randomValue ?? 0,
              data.filters,
            );
          },
          error: (error) => {
            this.error = 'Error fetching chart data';
            console.error('Error fetching chart data:', error.message);
          },
        }),
      );
      return;
    }
    //this is when we pass in an array of objects with filters and make an api call to get the data for each filter - we can send through as many objects as we like to make X bars on the chart
    else if (this.ref == 'dynamicArrayV2') {
      const randomValue = Math.floor(Math.random() * 100);
      this.subs.add(
        this.chartDataService.callApiForArrayV2(this.title, this.filters).subscribe({
          next: (data) => {
            this.chartDataArray = this.chartDataService.setDynamicChartDataArray(
              this.title,
              randomValue ?? 0,
              data.filters,
            );
          },
          error: (error) => {
            this.error = 'Error fetching chart data';
            console.error('Error fetching chart data:', error.message);
          },
        }),
      );
      return;
    }
    //this is when we pass in an array of objects with filters and make an api call to get the data for each filter - we can send through as many objects as we like to make X bars on the chart
    else if (this.ref == 'dynamicArrayV3') {
      const randomValue = Math.floor(Math.random() * 100);
      this.subs.add(
        this.chartDataService.callApiForArrayV3(this.title, this.filters).subscribe({
          next: (data) => {
            this.chartDataArray = this.chartDataService.setDynamicChartDataArray(
              this.title,
              randomValue ?? 0,
              data.filters,
            );
          },
          error: (error) => {
            this.error = 'Error fetching chart data';
            console.error('Error fetching chart data:', error.message);
          },
        }),
      );
      return;
    }
    //this is when we pass in an array of objects with filters and make an api call to get the data for each filter - we can send through as many objects as we like to make X bars on the chart
    else if (this.ref == 'dynamicArrayV4') {
      const randomValue = Math.floor(Math.random() * 100);
      this.subs.add(
        this.chartDataService
          .callApiForArrayV4(
            this.title,
            this.summary_search_parameter_for_charts,
            this.main_link_for_chart,
            this.filters,
          )
          .subscribe({
            next: (data) => {
              this.chartDataArray = this.chartDataService.setDynamicChartDataArray(
                this.title,
                randomValue ?? 0,
                data.filters,
              );
            },
            error: (error) => {
              this.error = 'Error fetching chart data';
              console.error('Error fetching chart data:', error.message);
            },
          }),
      );
      return;
    } else {
      this.chartDataArray = this.chartDataService.getChartDataArray();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
